import configuration from '@/configurations';
import {
  dify_chatbot_repository,
} from '@/repositories';
import {
  normalizeRetrieverResources,
  shouldDisplayWorkflowNodeStatus,
} from '@/utils/chatbotSuggestions';
import {
  loadChatbotSettings,
  normalizeChatbotSettings,
  saveChatbotSettings,
} from '@/utils/chatbotSettings';

let stream_abort_controller = null;
let message_id_counter = 0;

const USER_TOKEN_VARIABLE_NAME = 'user_token';
const CURRENT_PAGE_VARIABLE_NAME = 'current_page';
const ACCOUNT_ID_VARIABLE_NAME = 'account_id';
const ENVIRONMENT_VARIABLE_NAME = 'environment';
const VALUE_PROPOSITION_VARIABLE_NAME = 'value_proposition';
const CURRENT_JOB_VARIABLE_NAME = 'current_job';
const CURRENT_COMPANY_VARIABLE_NAME = 'current_company';
const ORGANIZATION_ID_VARIABLE_NAME = 'organization_id';
const DESK_V1_HOST_VARIABLE_NAME = 'desk_v1_host';
const DEFAULT_CHATBOT_ENVIRONMENT = 'production';
const PRODUCTION_API_HOSTNAME = 'desk-api.nomination.fr';

const getChatbotEnvironment = () => configuration.chatbot_environment;
const getDeskV1Host = () => configuration.desk_v1_host || '';

const resolveConversationEnvironment = (conversation) => {
  const value = conversation?.inputs?.[ENVIRONMENT_VARIABLE_NAME];

  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  return DEFAULT_CHATBOT_ENVIRONMENT;
};

const conversationMatchesEnvironment = (conversation, environment) => {
  const conversation_environment = resolveConversationEnvironment(conversation);

  if (conversation_environment === environment) {
    return true;
  }

  // Legacy conversations labeled "production" (or missing) match prod host.
  return conversation_environment === DEFAULT_CHATBOT_ENVIRONMENT
    && environment === PRODUCTION_API_HOSTNAME;
};

const filterConversationsByEnvironment = (conversations, environment) => (
  (conversations || []).filter(
    (conversation) => conversationMatchesEnvironment(conversation, environment)
  )
);

const UNIX_MS_THRESHOLD = 1e12;

/**
 * Normalize Dify created_at (unix seconds, ms, or ISO string) to milliseconds.
 *
 * @param {object} item
 * @returns {number|null}
 */
const getMessageCreatedAtMs = (item) => {
  const value = item?.created_at;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < UNIX_MS_THRESHOLD ? value * 1000 : value;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const as_number = Number(value);

  if (Number.isFinite(as_number)) {
    return as_number < UNIX_MS_THRESHOLD ? as_number * 1000 : as_number;
  }

  const as_date = Date.parse(value);

  if (Number.isFinite(as_date)) {
    return as_date;
  }

  return null;
};

/**
 * Sort conversation history oldest → newest. /messages order is not stable
 * across Dify versions and proxies, so created_at is the source of truth.
 *
 * @param {Array<object>} items
 * @returns {Array<object>}
 */
const sortHistoryByCreatedAt = (items) => {
  const history = (items || []).map((item, index) => ({ item, index }));

  history.sort((left, right) => {
    const left_ts = getMessageCreatedAtMs(left.item);
    const right_ts = getMessageCreatedAtMs(right.item);

    if (left_ts !== null && right_ts !== null && left_ts !== right_ts) {
      return left_ts - right_ts;
    }

    if (left_ts !== null && right_ts === null) {
      return -1;
    }

    if (left_ts === null && right_ts !== null) {
      return 1;
    }

    return left.index - right.index;
  });

  return history.map((entry) => entry.item);
};

const createLocalMessageId = (prefix = 'msg') => {
  message_id_counter += 1;

  return `${prefix}-${Date.now()}-${message_id_counter}`;
};

const abortCurrentStream = () => {
  if (stream_abort_controller) {
    stream_abort_controller.abort();
    stream_abort_controller = null;
  }
};

const initialState = () => ({
  is_open: false,
  show_conversations: false,
  messages: [],
  conversation_id: null,
  conversations: [],
  user_token_variable_ids: {},
  current_page_variable_ids: {},
  account_id_variable_ids: {},
  value_proposition_variable_ids: {},
  app_info: {
    name: '',
    description: '',
    tags: [],
    mode: '',
    author_name: '',
  },
  app_info_loaded: false,
  site: {
    title: '',
    show_workflow_steps: false,
  },
  site_loaded: false,
  parameters: {
    opening_statement: '',
    suggested_questions: [],
    suggested_questions_after_answer_enabled: false,
    retriever_resource_enabled: false,
  },
  parameters_loaded: false,
  is_loading: false,
  is_loading_conversations: false,
  is_streaming: false,
  stream_status: '',
  task_id: null,
  error: null,
  settings: loadChatbotSettings(),
});

const state = () => initialState();

const getters = {
  isOpen: (state) => state.is_open,
  showConversations: (state) => state.show_conversations,
  messages: (state) => state.messages,
  conversationId: (state) => state.conversation_id,
  conversations: (state) => state.conversations,
  appInfo: (state) => state.app_info,
  appInfoLoaded: (state) => state.app_info_loaded,
  site: (state) => state.site,
  siteLoaded: (state) => state.site_loaded,
  showWorkflowSteps: (state) => Boolean(state.site?.show_workflow_steps),
  parameters: (state) => state.parameters,
  parametersLoaded: (state) => state.parameters_loaded,
  isLoading: (state) => state.is_loading,
  isLoadingConversations: (state) => state.is_loading_conversations,
  isStreaming: (state) => state.is_streaming,
  streamStatus: (state) => state.stream_status,
  taskId: (state) => state.task_id,
  error: (state) => state.error,
  hasMessages: (state) => state.messages.length > 0,
  settings: (state) => state.settings,
  renderMarkdown: (state) => state.settings?.render_markdown !== false,
  rawWorkflowSteps: (state) => Boolean(state.settings?.raw_workflow_steps),
  dragOffsetX: (state) => Number(state.settings?.drag_offset_x) || 0,
};

const mutations = {
  set_is_open(state, is_open) {
    state.is_open = is_open;
  },
  set_show_conversations(state, show_conversations) {
    state.show_conversations = show_conversations;
  },
  set_messages(state, messages) {
    state.messages = messages;
  },
  push_message(state, message) {
    state.messages.push(message);
  },
  update_message(state, { id, patch }) {
    const index = state.messages.findIndex((message) => message.id === id);

    if (index === -1) {
      return;
    }

    state.messages.splice(index, 1, {
      ...state.messages[index],
      ...patch,
    });
  },
  append_message_content(state, { id, chunk }) {
    const index = state.messages.findIndex((message) => message.id === id);

    if (index === -1) {
      return;
    }

    const message = state.messages[index];

    state.messages.splice(index, 1, {
      ...message,
      content: `${message.content || ''}${chunk || ''}`,
    });
  },
  set_conversation_id(state, conversation_id) {
    state.conversation_id = conversation_id;
  },
  set_user_token_variable_id(state, { conversation_id, variable_id }) {
    if (!conversation_id || !variable_id) {
      return;
    }

    state.user_token_variable_ids = {
      ...state.user_token_variable_ids,
      [conversation_id]: variable_id,
    };
  },
  set_current_page_variable_id(state, { conversation_id, variable_id }) {
    if (!conversation_id || !variable_id) {
      return;
    }

    state.current_page_variable_ids = {
      ...state.current_page_variable_ids,
      [conversation_id]: variable_id,
    };
  },
  set_account_id_variable_id(state, { conversation_id, variable_id }) {
    if (!conversation_id || !variable_id) {
      return;
    }

    state.account_id_variable_ids = {
      ...state.account_id_variable_ids,
      [conversation_id]: variable_id,
    };
  },
  set_value_proposition_variable_id(state, { conversation_id, variable_id }) {
    if (!conversation_id || !variable_id) {
      return;
    }

    state.value_proposition_variable_ids = {
      ...state.value_proposition_variable_ids,
      [conversation_id]: variable_id,
    };
  },
  set_conversations(state, conversations) {
    state.conversations = conversations;
  },
  set_app_info(state, app_info) {
    state.app_info = {
      name: app_info?.name || '',
      description: app_info?.description || '',
      tags: app_info?.tags || [],
      mode: app_info?.mode || '',
      author_name: app_info?.author_name || '',
    };
  },
  set_app_info_loaded(state, app_info_loaded) {
    state.app_info_loaded = app_info_loaded;
  },
  set_site(state, site) {
    state.site = {
      title: site?.title || '',
      show_workflow_steps: Boolean(site?.show_workflow_steps),
    };
  },
  set_site_loaded(state, site_loaded) {
    state.site_loaded = site_loaded;
  },
  set_parameters(state, parameters) {
    const suggested_questions = (parameters?.suggested_questions || [])
      .filter((question) => typeof question === 'string' && question.trim());

    state.parameters = {
      opening_statement: parameters?.opening_statement || '',
      suggested_questions,
      suggested_questions_after_answer_enabled: Boolean(
        parameters?.suggested_questions_after_answer?.enabled
      ),
      retriever_resource_enabled: Boolean(
        parameters?.retriever_resource?.enabled
      ),
    };
  },
  set_parameters_loaded(state, parameters_loaded) {
    state.parameters_loaded = parameters_loaded;
  },
  set_is_loading(state, is_loading) {
    state.is_loading = is_loading;
  },
  set_is_loading_conversations(state, is_loading_conversations) {
    state.is_loading_conversations = is_loading_conversations;
  },
  set_is_streaming(state, is_streaming) {
    state.is_streaming = is_streaming;
  },
  set_stream_status(state, stream_status) {
    state.stream_status = stream_status || '';
  },
  set_task_id(state, task_id) {
    state.task_id = task_id;
  },
  set_error(state, error) {
    state.error = error;
  },
  set_settings(state, settings) {
    state.settings = normalizeChatbotSettings(settings);
  },
  set_drag_offset_x(state, drag_offset_x) {
    state.settings = normalizeChatbotSettings({
      ...state.settings,
      drag_offset_x,
    });
  },
  reset_state(state) {
    const { settings } = state;

    Object.assign(state, initialState());
    state.settings = settings;
  },
};

/**
 * SSE event types that must not update the UI. agent_log payloads can be multi-MB
 * (full tool I/O); parsing them freezes the main thread and the typing indicator.
 */
const IGNORED_STREAM_EVENT_NAMES = new Set([
  'agent_log',
  'ping',
]);

/**
 * Read the top-level SSE "event" field without JSON.parse.
 * Dify always puts "event" first in the payload object.
 *
 * @param {string} payload
 * @returns {string|null}
 */
const peekSseEventName = (payload) => {
  if (typeof payload !== 'string' || !payload) {
    return null;
  }

  const match = payload.match(/"event"\s*:\s*"([^"]+)"/);

  return match?.[1] || null;
};

const parseSseChunk = (raw_chunk) => {
  const trimmed = raw_chunk.trim();

  if (!trimmed || trimmed === '[DONE]') {
    return null;
  }

  // Keep-alive frames are `event: ping` with no data payload.
  if (/^event:\s*ping\s*$/im.test(trimmed) && !/^data:/im.test(trimmed)) {
    return { event: 'ping', _ignored: true };
  }

  const lines = trimmed.split(/\r?\n/);
  const data_lines = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.startsWith('data:')) {
      data_lines.push(line.replace(/^data:\s?/, ''));
      continue;
    }

    // Pretty-printed JSON may continue after a single data: line without repeating
    // the data: prefix on every line.
    if (
      data_lines.length
      && line
      && !line.startsWith('event:')
      && !line.startsWith('id:')
      && !line.startsWith('retry:')
      && !line.startsWith(':')
    ) {
      data_lines.push(line);
    }
  }

  let payload = data_lines.join('\n').trim();

  if (!payload && trimmed.startsWith('{')) {
    payload = trimmed;
  }

  if (!payload || payload === '[DONE]') {
    return null;
  }

  const peeked_event = peekSseEventName(payload);

  // Drop verbose / keep-alive events before JSON.parse — agent_log tool results
  // are enormous and would block the UI (typing indicator flatlines) for seconds.
  if (peeked_event && IGNORED_STREAM_EVENT_NAMES.has(peeked_event)) {
    return { event: peeked_event, _ignored: true };
  }

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

const extractStreamText = (event_data) => {
  const event_name = event_data?.event;

  if (event_name === 'message' || event_name === 'agent_message') {
    return event_data.answer || '';
  }

  if (event_name === 'text_chunk') {
    return event_data.data?.text || event_data.text || '';
  }

  return '';
};

const hasVisibleText = (value) => Boolean(String(value || '').trim());

const extractAnswerFromOutputs = (outputs = {}) => {
  if (typeof outputs.answer === 'string' && outputs.answer) {
    return outputs.answer;
  }

  if (typeof outputs.text === 'string' && outputs.text) {
    return outputs.text;
  }

  if (typeof outputs.result === 'string' && outputs.result) {
    return outputs.result;
  }

  return '';
};

const PROGRESSIVE_REVEAL_THRESHOLD = 20;

const yieldToUi = () => new Promise((resolve) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => resolve());

    return;
  }

  setTimeout(resolve, 0);
});

/**
 * Append text to a message. Small SSE deltas are applied as-is.
 * Large single payloads (Chatflow often buffers then dumps the full answer)
 * are revealed word-by-word so the UI still feels streaming.
 */
const appendContentToMessage = async ({
  commit,
  message_id,
  chunk,
  signal,
}) => {
  if (!chunk) {
    return;
  }

  if (chunk.length <= PROGRESSIVE_REVEAL_THRESHOLD) {
    commit('append_message_content', {
      id: message_id,
      chunk,
    });
    await yieldToUi();

    return;
  }

  const parts = chunk.split(/(\s+)/);

  for (let i = 0; i < parts.length; i += 1) {
    if (signal?.aborted) {
      return;
    }

    commit('append_message_content', {
      id: message_id,
      chunk: parts[i],
    });
    await yieldToUi();
  }
};

const readDifyStream = async ({
  response,
  onEvent,
}) => {
  if (!response?.body) {
    throw new Error('Invalid stream response');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let keep_reading = true;

  const flushMessageEndEvents = async (frames, start_index) => {
    for (let j = start_index; j < frames.length; j += 1) {
      const later_event = parseSseChunk(frames[j].trim());

      if (later_event?.event === 'message_end') {
        await onEvent(later_event);
      }
    }
  };

  try {
    while (keep_reading) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line (\n\n or \r\n\r\n)
      const parts = buffer.split(/\r?\n\r?\n/);

      buffer = parts.pop() || '';

      for (let i = 0; i < parts.length; i += 1) {
        const event_data = parseSseChunk(parts[i].trim());

        if (!event_data || event_data._ignored) {
          continue;
        }

        const should_continue = await onEvent(event_data);

        if (should_continue === false) {
          // message_end (citations) can share this read with an earlier stop event
          await flushMessageEndEvents(parts, i + 1);
          keep_reading = false;
          break;
        }
      }
    }

    if (keep_reading && buffer.trim()) {
      const event_data = parseSseChunk(buffer.trim());

      if (event_data && !event_data._ignored) {
        await onEvent(event_data);
      }
    }
  } finally {
    reader.releaseLock();
  }
};

const actions = {
  openChatbot({ commit }) {
    commit('set_is_open', true);
  },

  closeChatbot({ commit }) {
    commit('set_is_open', false);
    commit('set_show_conversations', false);
  },

  toggleChatbot({ state, commit, dispatch }) {
    if (state.is_open) {
      dispatch('closeChatbot');
    } else {
      commit('set_is_open', true);
    }
  },

  toggleConversationsPanel({ state, commit }) {
    commit('set_show_conversations', !state.show_conversations);
  },

  async fetchAppInfo({ commit, state }) {
    if (state.app_info_loaded) {
      return state.app_info;
    }

    try {
      const app_info = await dify_chatbot_repository.getInfo();

      commit('set_app_info', app_info || {});
      commit('set_app_info_loaded', true);

      return app_info;
    } catch (error) {
      commit('set_error', error.message);

      throw error;
    }
  },

  async fetchSiteSettings({ commit, state }) {
    if (state.site_loaded) {
      return state.site;
    }

    try {
      const site = await dify_chatbot_repository.getSite();

      commit('set_site', site || {});
      commit('set_site_loaded', true);

      return site;
    } catch (error) {
      commit('set_error', error.message);

      throw error;
    }
  },

  async fetchParameters({ commit, state }, { user }) {
    if (!user || state.parameters_loaded) {
      return state.parameters;
    }

    try {
      const parameters = await dify_chatbot_repository.getParameters({ user });

      commit('set_parameters', parameters || {});
      commit('set_parameters_loaded', true);

      return parameters;
    } catch (error) {
      commit('set_error', error.message);

      throw error;
    }
  },

  async deleteConversation({ commit, state, dispatch }, { conversation_id, user }) {
    if (!conversation_id || !user) {
      return;
    }

    await dify_chatbot_repository.deleteConversation({
      conversation_id,
      user,
    });

    const conversations = state.conversations
      .filter((conversation) => conversation.id !== conversation_id);

    commit('set_conversations', conversations);

    if (state.conversation_id === conversation_id) {
      await dispatch('startNewConversation');
    }
  },

  async fetchConversations({ commit }, { user, environment = getChatbotEnvironment() }) {
    if (!user) {
      return [];
    }

    commit('set_is_loading_conversations', true);

    try {
      const response = await dify_chatbot_repository.getConversations({ user });
      const conversations = filterConversationsByEnvironment(
        response?.data || [],
        environment
      );

      commit('set_conversations', conversations);

      return conversations;
    } catch (error) {
      commit('set_error', error.message);

      throw error;
    } finally {
      commit('set_is_loading_conversations', false);
    }
  },

  async loadConversation({ commit, dispatch }, { conversation_id, user }) {
    if (!conversation_id || !user) {
      return;
    }

    abortCurrentStream();
    commit('set_is_streaming', false);
    commit('set_stream_status', '');
    commit('set_task_id', null);
    commit('set_is_loading', true);
    commit('set_show_conversations', false);

    try {
      const response = await dify_chatbot_repository.getMessages({
        conversation_id,
        user,
        limit: 100,
      });
      const history = sortHistoryByCreatedAt(response?.data || []);
      const messages = [];

      history.forEach((item) => {
        if (item.query) {
          messages.push({
            id: `${item.id}-user`,
            role: 'user',
            content: item.query,
            status: 'done',
          });
        }

        if (item.answer) {
          messages.push({
            id: `${item.id}-assistant`,
            role: 'assistant',
            content: item.answer,
            status: 'done',
            dify_message_id: item.id,
            suggested_questions: [],
            retriever_resources: normalizeRetrieverResources(
              item.retriever_resources
              || item.metadata?.retriever_resources
            ),
          });
        }
      });

      commit('set_conversation_id', conversation_id);
      commit('set_messages', messages);
    } catch (error) {
      commit('set_error', error.message);

      throw error;
    } finally {
      commit('set_is_loading', false);
      dispatch('fetchConversations', { user }).catch(() => {});
    }
  },

  async openConversationsMenu({ commit, dispatch }, { user, environment }) {
    commit('set_show_conversations', true);

    if (!user) {
      return [];
    }

    return dispatch('fetchConversations', { user, environment });
  },

  closeConversationsMenu({ commit }) {
    commit('set_show_conversations', false);
  },

  /**
   * Fetch Dify follow-up questions for an assistant message when the feature
   * is enabled. Fails silently if disabled or the model returns nothing.
   */
  async fetchFollowUpQuestions({ commit, state }, { local_message_id, user }) {
    if (
      !local_message_id
      || !user
      || !state.settings?.show_follow_up_suggestions
      || !state.parameters?.suggested_questions_after_answer_enabled
    ) {
      return [];
    }

    const message = state.messages.find(
      (item) => item.id === local_message_id
    );
    const dify_message_id = message?.dify_message_id;

    if (!dify_message_id) {
      return [];
    }

    try {
      const response = await dify_chatbot_repository.getSuggestedQuestions({
        message_id: dify_message_id,
        user,
      });
      const suggested_questions = (response?.data || [])
        .filter((question) => typeof question === 'string' && question.trim());

      // Drop results if the conversation moved on while the request was in flight.
      const last_assistant = [...state.messages]
        .reverse()
        .find((item) => item.role === 'assistant');

      if (
        !last_assistant
        || last_assistant.id !== local_message_id
        || last_assistant.dify_message_id !== dify_message_id
      ) {
        return [];
      }

      commit('update_message', {
        id: local_message_id,
        patch: { suggested_questions },
      });

      return suggested_questions;
    } catch {
      return [];
    }
  },

  async startNewConversation({ commit }) {
    abortCurrentStream();
    commit('set_is_streaming', false);
    commit('set_task_id', null);
    commit('set_conversation_id', null);
    commit('set_messages', []);
    commit('set_show_conversations', false);
    commit('set_error', null);
  },

  async resolveUserTokenVariableId({ commit, state }, { conversation_id, user }) {
    const cached_variable_id = state.user_token_variable_ids[conversation_id];

    if (cached_variable_id) {
      return cached_variable_id;
    }

    const response = await dify_chatbot_repository.getConversationVariables({
      conversation_id,
      user,
      variable_name: USER_TOKEN_VARIABLE_NAME,
    });

    const variable = (response?.data || []).find(
      (item) => item?.name === USER_TOKEN_VARIABLE_NAME
    );

    if (!variable?.id) {
      throw new Error('user_token conversation variable not found');
    }

    commit('set_user_token_variable_id', {
      conversation_id,
      variable_id: variable.id,
    });

    return variable.id;
  },

  async resolveCurrentPageVariableId({ commit, state }, { conversation_id, user }) {
    const cached_variable_id = state.current_page_variable_ids[conversation_id];

    if (cached_variable_id) {
      return cached_variable_id;
    }

    const response = await dify_chatbot_repository.getConversationVariables({
      conversation_id,
      user,
      variable_name: CURRENT_PAGE_VARIABLE_NAME,
    });

    const variable = (response?.data || []).find(
      (item) => item?.name === CURRENT_PAGE_VARIABLE_NAME
    );

    if (!variable?.id) {
      throw new Error('current_page conversation variable not found');
    }

    commit('set_current_page_variable_id', {
      conversation_id,
      variable_id: variable.id,
    });

    return variable.id;
  },

  async refreshConversationUserToken(
    { dispatch },
    { conversation_id, user, user_token }
  ) {
    if (!conversation_id || !user || !user_token) {
      return;
    }

    const variable_id = await dispatch('resolveUserTokenVariableId', {
      conversation_id,
      user,
    });

    await dify_chatbot_repository.updateConversationVariable({
      conversation_id,
      variable_id,
      user,
      value: user_token,
    });
  },

  async refreshConversationCurrentPage(
    { dispatch },
    { conversation_id, user, current_page }
  ) {
    if (!conversation_id || !user || !current_page) {
      return;
    }

    const variable_id = await dispatch('resolveCurrentPageVariableId', {
      conversation_id,
      user,
    });

    await dify_chatbot_repository.updateConversationVariable({
      conversation_id,
      variable_id,
      user,
      value: current_page,
    });
  },

  async resolveAccountIdVariableId({ commit, state }, { conversation_id, user }) {
    const cached_variable_id = state.account_id_variable_ids[conversation_id];

    if (cached_variable_id) {
      return cached_variable_id;
    }

    const response = await dify_chatbot_repository.getConversationVariables({
      conversation_id,
      user,
      variable_name: ACCOUNT_ID_VARIABLE_NAME,
    });

    const variable = (response?.data || []).find(
      (item) => item?.name === ACCOUNT_ID_VARIABLE_NAME
    );

    if (!variable?.id) {
      throw new Error('account_id conversation variable not found');
    }

    commit('set_account_id_variable_id', {
      conversation_id,
      variable_id: variable.id,
    });

    return variable.id;
  },

  async refreshConversationAccountId(
    { dispatch },
    { conversation_id, user, account_id }
  ) {
    if (!conversation_id || !user || account_id === undefined || account_id === null) {
      return;
    }

    const variable_id = await dispatch('resolveAccountIdVariableId', {
      conversation_id,
      user,
    });

    await dify_chatbot_repository.updateConversationVariable({
      conversation_id,
      variable_id,
      user,
      value: account_id,
    });
  },

  async resolveValuePropositionVariableId({ commit, state }, { conversation_id, user }) {
    const cached_variable_id = state.value_proposition_variable_ids[conversation_id];

    if (cached_variable_id) {
      return cached_variable_id;
    }

    const response = await dify_chatbot_repository.getConversationVariables({
      conversation_id,
      user,
      variable_name: VALUE_PROPOSITION_VARIABLE_NAME,
    });

    const variable = (response?.data || []).find(
      (item) => item?.name === VALUE_PROPOSITION_VARIABLE_NAME
    );

    if (!variable?.id) {
      throw new Error('value_proposition conversation variable not found');
    }

    commit('set_value_proposition_variable_id', {
      conversation_id,
      variable_id: variable.id,
    });

    return variable.id;
  },

  async refreshConversationValueProposition(
    { dispatch },
    { conversation_id, user, value_proposition }
  ) {
    if (!conversation_id || !user || value_proposition === undefined
      || value_proposition === null) {
      return;
    }

    const variable_id = await dispatch('resolveValuePropositionVariableId', {
      conversation_id,
      user,
    });

    await dify_chatbot_repository.updateConversationVariable({
      conversation_id,
      variable_id,
      user,
      value: value_proposition,
    });
  },

  async sendMessage({ commit, state, dispatch }, { query, user, inputs = {} }) {
    const trimmed_query = (query || '').trim();

    if (!trimmed_query || !user || state.is_streaming) {
      return;
    }

    abortCurrentStream();
    stream_abort_controller = new AbortController();

    const user_message_id = createLocalMessageId('user');
    const assistant_message_id = createLocalMessageId('assistant');
    const is_new_conversation = !state.conversation_id;
    // environment, current_job, current_company, organization_id and desk_v1_host
    // are set once at conversation start. user_token, current_page, account_id and
    // value_proposition are sent on every message and refreshed via conversation
    // variables on follow-ups.
    const message_inputs = {
      user_token: inputs.user_token,
      current_page: inputs.current_page,
      account_id: inputs.account_id ?? 0,
      [VALUE_PROPOSITION_VARIABLE_NAME]: inputs.value_proposition || '',
    };

    if (is_new_conversation) {
      message_inputs[ENVIRONMENT_VARIABLE_NAME] = inputs.environment
        || getChatbotEnvironment();
      message_inputs[CURRENT_JOB_VARIABLE_NAME] = inputs.current_job || '';
      message_inputs[CURRENT_COMPANY_VARIABLE_NAME] = inputs.current_company || '';
      message_inputs[ORGANIZATION_ID_VARIABLE_NAME] = inputs.organization_id ?? 0;
      message_inputs[DESK_V1_HOST_VARIABLE_NAME] = inputs.desk_v1_host
        || getDeskV1Host();
    }

    commit('set_error', null);
    commit('set_is_streaming', true);
    commit('set_stream_status', '');

    // Hide previous follow-ups as soon as the user sends a new message.
    state.messages.forEach((message) => {
      if (message.suggested_questions?.length) {
        commit('update_message', {
          id: message.id,
          patch: { suggested_questions: [] },
        });
      }
    });

    try {
      // Existing conversations keep start-node inputs frozen except user_token,
      // current_page, account_id and value_proposition, which are refreshed
      // before each follow-up message.
      if (state.conversation_id) {
        await dispatch('refreshConversationUserToken', {
          conversation_id: state.conversation_id,
          user,
          user_token: inputs.user_token,
        });
        await dispatch('refreshConversationCurrentPage', {
          conversation_id: state.conversation_id,
          user,
          current_page: inputs.current_page,
        });
        await dispatch('refreshConversationAccountId', {
          conversation_id: state.conversation_id,
          user,
          account_id: message_inputs.account_id,
        });
        await dispatch('refreshConversationValueProposition', {
          conversation_id: state.conversation_id,
          user,
          value_proposition: message_inputs[VALUE_PROPOSITION_VARIABLE_NAME],
        });
      }

      commit('push_message', {
        id: user_message_id,
        role: 'user',
        content: trimmed_query,
        status: 'done',
      });
      commit('push_message', {
        id: assistant_message_id,
        role: 'assistant',
        content: '',
        status: 'streaming',
        dify_message_id: null,
        suggested_questions: [],
        retriever_resources: [],
      });

      const response = await dify_chatbot_repository.sendChatMessage({
        query: trimmed_query,
        user,
        conversation_id: state.conversation_id || '',
        inputs: message_inputs,
        signal: stream_abort_controller.signal,
      });

      let has_message_chunks = false;
      let dify_message_id = null;
      const getAssistantContent = () => state.messages.find(
        (message) => message.id === assistant_message_id
      )?.content || '';
      const hasAssistantVisibleContent = () => hasVisibleText(getAssistantContent());

      await readDifyStream({
        response,
        onEvent: async (event_data) => {
          const event_name = event_data.event;
          const signal = stream_abort_controller?.signal;

          // Skipped at parse time (no JSON.parse) or as a safety net — never
          // touch stream_status / message content for these.
          if (
            event_data._ignored
            || IGNORED_STREAM_EVENT_NAMES.has(event_name)
          ) {
            return true;
          }

          if (event_data.task_id) {
            commit('set_task_id', event_data.task_id);
          }

          if (event_data.conversation_id) {
            commit('set_conversation_id', event_data.conversation_id);
          }

          if (event_data.message_id && event_data.message_id !== dify_message_id) {
            dify_message_id = event_data.message_id;
            commit('update_message', {
              id: assistant_message_id,
              patch: { dify_message_id },
            });
          }

          if (event_name === 'node_started') {
            const node_type = event_data.data?.node_type;
            const title = event_data.data?.title;

            if (
              title
              && shouldDisplayWorkflowNodeStatus(node_type)
            ) {
              commit('set_stream_status', title);
            }

            return true;
          }

          if (event_name === 'message' || event_name === 'agent_message') {
            const chunk = extractStreamText(event_data);

            // Spaces and newlines often arrive as their own SSE tokens. Dropping
            // them glues words together (`###3.` / `prospectionJe`) while history
            // still looks correct because Dify stores the full answer.
            // Whitespace-only chunks must still not clear the loading label.
            if (!chunk) {
              return true;
            }

            if (hasVisibleText(chunk)) {
              has_message_chunks = true;
              commit('set_stream_status', '');
            }

            await appendContentToMessage({
              commit,
              message_id: assistant_message_id,
              chunk,
              signal,
            });

            return true;
          }

          // Chatflow may stream LLM tokens as text_chunk when Answer is wired for streaming
          if (event_name === 'text_chunk' && !has_message_chunks) {
            const chunk = extractStreamText(event_data);

            if (!chunk) {
              return true;
            }

            if (hasVisibleText(chunk)) {
              commit('set_stream_status', '');
            }

            await appendContentToMessage({
              commit,
              message_id: assistant_message_id,
              chunk,
              signal,
            });

            return true;
          }

          if (event_name === 'message_replace') {
            const replaced_content = event_data.answer || '';

            if (!replaced_content || !String(replaced_content).trim()) {
              return true;
            }

            has_message_chunks = true;
            commit('set_stream_status', '');
            commit('update_message', {
              id: assistant_message_id,
              patch: {
                content: replaced_content,
              },
            });
            await yieldToUi();

            return true;
          }

          // When intermediate nodes block token streaming, the full answer often
          // arrives once on the Answer node's node_finished event.
          if (event_name === 'node_finished') {
            const node_type = event_data.data?.node_type;
            const node_status = event_data.data?.status;
            const answer_text = extractAnswerFromOutputs(event_data.data?.outputs);

            if (node_status === 'failed' && event_data.data?.error) {
              commit('set_error', event_data.data.error);
            }

            if (
              !has_message_chunks
              && !hasAssistantVisibleContent()
              && node_type === 'answer'
              && answer_text
            ) {
              has_message_chunks = true;
              commit('set_stream_status', '');
              await appendContentToMessage({
                commit,
                message_id: assistant_message_id,
                chunk: answer_text,
                signal,
              });
            }

            return true;
          }

          if (event_name === 'workflow_finished') {
            const workflow_status = event_data.data?.status;
            const answer_text = extractAnswerFromOutputs(event_data.data?.outputs);

            if (workflow_status === 'failed') {
              const error_message = event_data.data?.error || 'Workflow failed';

              commit('set_error', error_message);

              if (!hasAssistantVisibleContent()) {
                commit('update_message', {
                  id: assistant_message_id,
                  patch: {
                    status: 'error',
                    content: error_message,
                  },
                });
              }

              return false;
            }

            if (!has_message_chunks && !hasAssistantVisibleContent() && answer_text) {
              commit('set_stream_status', '');
              await appendContentToMessage({
                commit,
                message_id: assistant_message_id,
                chunk: answer_text,
                signal,
              });
            }

            commit('update_message', {
              id: assistant_message_id,
              patch: { status: 'done' },
            });

            // Keep reading: message_end (with citations) can arrive after this.
            return true;
          }

          if (event_name === 'message_end') {
            // Documented Citations & Attributions source (same as Dify web client):
            // message_end.metadata.retriever_resources
            const retriever_resources = normalizeRetrieverResources(
              event_data.metadata?.retriever_resources
            );

            commit('set_stream_status', '');
            commit('update_message', {
              id: assistant_message_id,
              patch: {
                status: 'done',
                retriever_resources,
              },
            });

            return true;
          }

          if (event_name === 'error') {
            const error_message = event_data.message || 'Stream error';

            commit('update_message', {
              id: assistant_message_id,
              patch: {
                status: 'error',
              },
            });
            commit('set_error', error_message);

            return false;
          }

          return true;
        },
      });

      const assistant_message = state.messages.find(
        (message) => message.id === assistant_message_id
      );

      if (assistant_message?.status === 'streaming') {
        commit('update_message', {
          id: assistant_message_id,
          patch: { status: 'done' },
        });
      }

      const latest_assistant = state.messages.find(
        (message) => message.id === assistant_message_id
      );

      if (state.error && !latest_assistant?.content) {
        throw new Error(state.error);
      }

      if (state.error && latest_assistant?.status === 'error') {
        throw new Error(state.error);
      }

      if (latest_assistant?.status === 'done' && dify_message_id) {
        dispatch('fetchFollowUpQuestions', {
          local_message_id: assistant_message_id,
          user,
        }).catch(() => {});
      }

      dispatch('fetchConversations', { user }).catch(() => {});
    } catch (error) {
      if (error?.name === 'AbortError') {
        commit('update_message', {
          id: assistant_message_id,
          patch: { status: 'done' },
        });

        return;
      }

      commit('set_error', error.message);

      const assistant_message = state.messages.find(
        (message) => message.id === assistant_message_id
      );

      if (assistant_message) {
        commit('update_message', {
          id: assistant_message_id,
          patch: {
            status: 'error',
            content: error.message,
          },
        });
      }

      throw error;
    } finally {
      stream_abort_controller = null;
      commit('set_is_streaming', false);
      commit('set_stream_status', '');
      commit('set_task_id', null);
    }
  },

  async stopGeneration({ commit, state }, { user }) {
    const { task_id } = state;

    abortCurrentStream();

    if (task_id && user) {
      try {
        await dify_chatbot_repository.stopChatMessage({
          task_id,
          user,
        });
      } catch {
        // Ignore stop errors; local abort already stopped the UI stream
      }
    }

    commit('set_is_streaming', false);
    commit('set_task_id', null);

    const last_assistant = [...state.messages]
      .reverse()
      .find((message) => message.role === 'assistant');

    if (last_assistant && last_assistant.status === 'streaming') {
      commit('update_message', {
        id: last_assistant.id,
        patch: { status: 'done' },
      });
    }
  },

  resetOnLogout({ commit }) {
    abortCurrentStream();
    commit('reset_state');
  },

  updateSettings({ commit, state }, patch = {}) {
    const next_settings = saveChatbotSettings({
      ...state.settings,
      ...patch,
    });

    commit('set_settings', next_settings);

    return next_settings;
  },

  /**
   * A new API key can point to another Dify app: drop the cached app payloads
   * and the current chat so the caller can refetch everything from scratch.
   */
  async applyApiKeyChange({ commit, dispatch }, { api_key } = {}) {
    await dispatch('updateSettings', { api_key });

    commit('set_app_info_loaded', false);
    commit('set_site_loaded', false);
    commit('set_parameters_loaded', false);
    commit('set_conversations', []);

    return dispatch('startNewConversation');
  },

  updateDragOffset({ commit, state }, { offset, persist = false } = {}) {
    const drag_offset_x = Number.isFinite(offset) ? offset : 0;

    commit('set_drag_offset_x', drag_offset_x);

    if (persist) {
      saveChatbotSettings({
        ...state.settings,
        drag_offset_x,
      });
    }

    return drag_offset_x;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
